import { relations } from "drizzle-orm";
import {
  integer,
  index,
  pgEnum,
  pgTable,
  primaryKey,
  text,
  timestamp,
  unique,
} from "drizzle-orm/pg-core";

import { generateId } from "@workspace/shared/utils";

import {
  createInsertSchema,
  createSelectSchema,
  createUpdateSchema,
} from "../lib/zod";

import { organization, user } from "./auth";
import { survey } from "./surveys";

import type * as z from "zod";

export const subscriptionStatusEnum = pgEnum("subscription_status", [
  "active",
  "canceled",
  "incomplete",
  "incomplete_expired",
  "past_due",
  "paused",
  "trialing",
  "unpaid",
]);

export const paymentStatusEnum = pgEnum("payment_status", [
  "pending",
  "succeeded",
  "failed",
]);

export const organizationBillingPlanEnum = pgEnum("organization_billing_plan", [
  "starter",
  "professional",
  "practice",
  "internal",
]);

export const customer = pgTable(
  "customer",
  {
    id: text().primaryKey().$defaultFn(generateId),
    referenceId: text().notNull(),
    externalId: text().notNull(),
    provider: text().notNull(),
    createdAt: timestamp().notNull().defaultNow(),
    updatedAt: timestamp()
      .notNull()
      .$onUpdate(() => new Date()),
  },
  (t) => [
    unique().on(t.referenceId, t.provider),
    unique().on(t.externalId, t.provider),
  ],
);

export const subscription = pgTable(
  "subscription",
  {
    id: text().primaryKey().$defaultFn(generateId),
    customerId: text()
      .references(() => customer.id, {
        onDelete: "cascade",
      })
      .notNull(),
    externalId: text().notNull(),
    variantId: text().notNull(),
    status: subscriptionStatusEnum().notNull(),
    store: text().notNull(),
    periodStartsAt: timestamp().notNull(),
    periodEndsAt: timestamp().notNull(),
    trialStartsAt: timestamp(),
    trialEndsAt: timestamp(),
    createdAt: timestamp().notNull().defaultNow(),
    updatedAt: timestamp()
      .notNull()
      .$onUpdate(() => new Date()),
  },
  (t) => [unique().on(t.externalId, t.store)],
);

export const order = pgTable(
  "order",
  {
    id: text().primaryKey().$defaultFn(generateId),
    customerId: text()
      .references(() => customer.id, {
        onDelete: "cascade",
      })
      .notNull(),
    externalId: text().notNull(),
    variantId: text().notNull(),
    status: paymentStatusEnum().notNull(),
    store: text().notNull(),
    createdAt: timestamp().notNull().defaultNow(),
    updatedAt: timestamp()
      .notNull()
      .$onUpdate(() => new Date()),
  },
  (t) => [unique().on(t.externalId, t.store)],
);

export const organizationSubscription = pgTable(
  "organization_subscription",
  {
    id: text().primaryKey().$defaultFn(generateId),
    organizationId: text()
      .references(() => organization.id, {
        onDelete: "cascade",
      })
      .notNull(),
    provider: text().notNull().default("paypal"),
    plan: organizationBillingPlanEnum().notNull(),
    status: subscriptionStatusEnum().notNull(),
    paypalSubscriptionId: text(),
    billingPeriodStartsAt: timestamp(),
    billingPeriodEndsAt: timestamp(),
    annualReportAllowance: integer().notNull(),
    reportsUsed: integer().notNull().default(0),
    createdAt: timestamp().notNull().defaultNow(),
    updatedAt: timestamp()
      .notNull()
      .$onUpdate(() => new Date()),
  },
  (t) => [unique().on(t.organizationId), unique().on(t.paypalSubscriptionId)],
);

/**
 * One row records that a distinct survey has consumed an organization's free
 * report allowance. The five-row limit is enforced transactionally by the
 * billing service, while this composite key makes regeneration idempotent.
 */
export const organizationFreeSurvey = pgTable(
  "organization_free_survey",
  {
    organizationId: text("organization_id")
      .notNull()
      .references(() => organization.id, { onDelete: "cascade" }),
    surveyId: text("survey_id")
      .notNull()
      .references(() => survey.id, { onDelete: "cascade" }),
    consumedByUserId: text("consumed_by_user_id").references(() => user.id, {
      onDelete: "set null",
    }),
    consumedAt: timestamp("consumed_at").notNull().defaultNow(),
  },
  (table) => [
    primaryKey({ columns: [table.organizationId, table.surveyId] }),
    index("organization_free_survey_organization_id_idx").on(
      table.organizationId,
    ),
  ],
);

export const customerRelations = relations(customer, ({ many }) => ({
  subscriptions: many(subscription),
  orders: many(order),
}));

export const subscriptionRelations = relations(subscription, ({ one }) => ({
  customer: one(customer, {
    fields: [subscription.customerId],
    references: [customer.id],
  }),
}));

export const orderRelations = relations(order, ({ one }) => ({
  customer: one(customer, {
    fields: [order.customerId],
    references: [customer.id],
  }),
}));

export const organizationSubscriptionRelations = relations(
  organizationSubscription,
  ({ one }) => ({
    organization: one(organization, {
      fields: [organizationSubscription.organizationId],
      references: [organization.id],
    }),
  }),
);

export const organizationFreeSurveyRelations = relations(
  organizationFreeSurvey,
  ({ one }) => ({
    organization: one(organization, {
      fields: [organizationFreeSurvey.organizationId],
      references: [organization.id],
    }),
    survey: one(survey, {
      fields: [organizationFreeSurvey.surveyId],
      references: [survey.id],
    }),
    consumedByUser: one(user, {
      fields: [organizationFreeSurvey.consumedByUserId],
      references: [user.id],
    }),
  }),
);

export const insertCustomerSchema = createInsertSchema(customer);
export const selectCustomerSchema = createSelectSchema(customer);
export const updateCustomerSchema = createUpdateSchema(customer);

export const insertSubscriptionSchema = createInsertSchema(subscription);
export const selectSubscriptionSchema = createSelectSchema(subscription);
export const updateSubscriptionSchema = createUpdateSchema(subscription);

export const insertOrderSchema = createInsertSchema(order);
export const selectOrderSchema = createSelectSchema(order);
export const updateOrderSchema = createUpdateSchema(order);

export const insertOrganizationSubscriptionSchema = createInsertSchema(
  organizationSubscription,
);
export const selectOrganizationSubscriptionSchema = createSelectSchema(
  organizationSubscription,
);
export const updateOrganizationSubscriptionSchema = createUpdateSchema(
  organizationSubscription,
);

export type SelectCustomer = z.infer<typeof selectCustomerSchema>;
export type InsertCustomer = z.infer<typeof insertCustomerSchema>;
export type UpdateCustomer = z.infer<typeof updateCustomerSchema>;

export type SelectSubscription = z.infer<typeof selectSubscriptionSchema>;
export type InsertSubscription = z.infer<typeof insertSubscriptionSchema>;
export type UpdateSubscription = z.infer<typeof updateSubscriptionSchema>;

export type SelectOrder = z.infer<typeof selectOrderSchema>;
export type InsertOrder = z.infer<typeof insertOrderSchema>;
export type UpdateOrder = z.infer<typeof updateOrderSchema>;

export type SelectOrganizationSubscription = z.infer<
  typeof selectOrganizationSubscriptionSchema
>;
export type InsertOrganizationSubscription = z.infer<
  typeof insertOrganizationSubscriptionSchema
>;
export type UpdateOrganizationSubscription = z.infer<
  typeof updateOrganizationSubscriptionSchema
>;
